import React, { useEffect, useState } from 'react';
import c from '../../constant';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Button,
	Box,
	Flex,
	useToast,
	Wrap,
	FormLabel,
	WrapItem,
	Avatar,
	Text,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TextFormField from '../form/TextFormField';
import DateRangeFormField from '../form/DateRangeFormField';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import RadioFormField from '../form/RadioFormField';
import SelectUserAllModal from '../../components/Modal/SelectUserShowAllModal';
import 'moment/locale/en-gb';
import LabelDetail from '../LabelDetail';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare, faPlusSquare, faSync } from '@fortawesome/free-solid-svg-icons';
import useStore from '../../store';
import CheckBoxFormField from '../form/CheckBoxFormField';
import SelectFormField from '../form/SelectFormField';

const filterSchema = Yup.object().shape({
	no: Yup.number(),
	officials: Yup.string(),
	organiser: Yup.string(),
	department: Yup.string(),
	office: Yup.string(),
	trainingLocation: Yup.string(),
	courseTitle: Yup.string(),
	createdDate: Yup.string(),
	unreadOnly: Yup.boolean(),
	status: Yup.string(),
	means: Yup.string(),
	locationType: Yup.string(),
	type: Yup.string(),
	bioListing: Yup.boolean(),
});

function TrainingCourseFilter({
	isOpen,
	onClose,
	callback,
	officials = '',
	officialName = '',
	officialNameKhmer = '',
	officialAvatar = '',
	means = '',
	no = '',
	trainingLocation = '',
	organiser = '',
	department = '',
	office = '',
	courseTitle = '',
	type = '',
	locationType = '',
	dateStart = null,
	dateEnd = null,
	bioListing = null,
	read = null,
}) {
	moment.locale('en-gb');
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const [openSelectUserAllModel, setOpenSelectUserAllModel] = useState(false);
	const [selectedOfficials, setSelectedOfficials] = useState(null);

	useEffect(() => {
		if (officials) {
			setSelectedOfficials({
				id: officials,
				fullName: officialName,
				fullNameKhmer: officialNameKhmer,
				avatar: officialAvatar
					? {
							icon: officialAvatar,
						}
					: null,
			});
		} else {
			setSelectedOfficials(null);
		}
	}, [officials, officialName, officialNameKhmer, officialAvatar]);

	let defaultRange = '';
	if (dateStart && dateEnd) {
		const defaultFrom = JSON.parse(dateStart);

		const defaultTo = JSON.parse(dateEnd);
		defaultRange = {
			from: defaultFrom,
			to: defaultTo,
		};
	}

	const showOfficialFilter = user?.canSubmitTrainingCourse === true;

	const OFFICE_OPTIONS_BY_DEPARTMENT = {
		D_G_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យរដ្ឋបាល' : 'Administrative', value: 'administrative' },
			{ label: lang === 'kh' ? 'ការិយាល័យគណនេយ្យ' : 'Accounting', value: 'accounting' },
			{ label: lang === 'kh' ? 'ការិយាល័យធនធានមនុស្ស' : 'Human Resource', value: 'human_resource' },
			{ label: lang === 'kh' ? 'ការិយាល័យទំនាក់ទំនងសាធារណៈ' : 'Public Relation', value: 'public_relation' },
		],

		D_L_L_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ' : 'Licensing', value: 'licensing' },
			{ label: lang === 'kh' ? 'ការិយាល័យចុះបញ្ជីអន្តរការី' : 'Intermediary Registration', value: 'intermediary_registration' },
			{ label: lang === 'kh' ? 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ' : 'Legal and Mediation', value: 'legal_and_mediation' },
		],

		D_S: [
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurer', value: 'life_insurer' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ' : 'General Insurer', value: 'general_insurer' },
			{
				label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង' : 'micro_insurer',
				value: 'office10',
			},
			{ label: lang === 'kh' ? 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន' : 'Prudential', value: 'prudential' },
		],

		D_M_D: [
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurance Product', value: 'life_insurance_product' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ' : 'General Insurance Product', value: 'general_insurance_product' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច' : 'Micro Insurance Product', value: 'micro_insurance_product' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ' : 'Market Conduct Supervision', value: 'market_conduct_supervision' },
		],

		D_R_T_C: [
			{ label: lang === 'kh' ? 'ការិយាល័យស្រាវជ្រាវ' : 'Research', value: 'research' },
			{ label: lang === 'kh' ? 'ការិយាល័យបណ្តុះបណ្តាល' : 'Training', value: 'training' },
			{ label: lang === 'kh' ? 'ការិយាល័យសហប្រតិបត្តិការ' : 'Cooperation', value: 'cooperation' },
			{ label: lang === 'kh' ? 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង' : 'InsurTech', value: 'insurtech' },
		],
	};

	return (
		<>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							initialValues={{
								no: no ? no : '',
								officials: officials ? officials : '',
								organiser: organiser ? organiser : '',
								courseTitle: courseTitle ? courseTitle : '',
								type: type ? type : '',
								locationType: locationType ? locationType : '',
								means: means ? means : '',
								department: department ? department : '',
								office: office ? office : '',
								trainingLocation: trainingLocation ? trainingLocation : '',
								createdDate: defaultRange ? JSON.stringify(defaultRange) : '',
								bioListing: bioListing ? bioListing : '',
							}}
							validationSchema={filterSchema}
							onSubmit={(values) => {
								let url = '?page=1';
								const officeList = OFFICE_OPTIONS_BY_DEPARTMENT[values?.department] || [];
								const isOfficeValidForDepartment = officeList?.some((item) => item.value === values?.office);

								if (values?.officials) {
									url += `&officials=${values.officials}`;
								}

								if (values?.courseTitle) {
									url += `&courseTitle=${values.courseTitle}`;
								}

								if (values?.department) {
									url += `&department=${values?.department}`;
								}

								if (values?.department && values?.office && isOfficeValidForDepartment) {
									url += `&office=${values?.office}`;
								}

								if (selectedOfficials?.fullName) {
									url += `&officialName=${selectedOfficials.fullName}`;
								}

								if (selectedOfficials?.fullNameKhmer) {
									url += `&officialNameKhmer=${selectedOfficials.fullNameKhmer}`;
								}

								if (selectedOfficials?.avatar?.icon) {
									url += `&officialAvatar=${selectedOfficials.avatar.icon}`;
								}

								if (values?.no) {
									url += `&no=${values?.no}`;
								}

								if (values?.organiser) {
									url += `&organiser=${values?.organiser}`;
								}

								if (values?.trainingLocation) {
									url += `&location=${values?.trainingLocation}`;
								}

								if (values?.createdDate) {
									const tmpDate = JSON.parse(values?.createdDate);
									if (tmpDate?.from && tmpDate?.to) {
										const dateStart = JSON.stringify(tmpDate.from);
										const dateEnd = JSON.stringify(tmpDate.to);
										url += `&dateStart=${dateStart}&dateEnd=${dateEnd}`;
									}
								}

								if (values?.means) {
									url += `&means=${values?.means}`;
								}

								if (values?.type) {
									url += `&type=${values?.type}`;
								}

								if (values?.locationType) {
									url += `&locationType=${values?.locationType}`;
								}

								if (values?.bioListing) {
									url += `&bioListing=${values?.bioListing}`;
								}
								callback(url);
								onClose();
							}}
						>
							{(props) => (
								<Box pb="4">
									<Form>
										{showOfficialFilter && (
											<>
												<FormLabel>{t('Officials')}</FormLabel>

												<Flex>
													<LabelDetail
														mb={4}
														mt={0}
														labelWidth="169px"
														childDetail={
															<Wrap>
																{selectedOfficials && (
																	<Flex
																		direction="column"
																		justifyContent="center"
																		alignItems="center"
																		key={selectedOfficials?.id}
																		mr={2}
																	>
																		<WrapItem className="avatarWraper">
																			<FontAwesomeIcon
																				onClick={() => {
																					setSelectedOfficials(null);
																					props.setFieldValue('officials', '');

																					toast({
																						title: t('You have removed a user'),
																						status: 'warning',
																						isClosable: true,
																						position: 'top-right',
																					});
																				}}
																				className="remove"
																				icon={faMinusSquare}
																				style={{
																					fontSize: 18,
																					display: 'none',
																					position: 'absolute',
																					zIndex: '100',
																					marginLeft: 32,
																					color: 'red',
																					backgroundColor: 'white',
																					padding: 2,
																					cursor: 'pointer',
																				}}
																			/>

																			<Avatar
																				name={selectedOfficials?.fullNameKhmer || selectedOfficials?.fullName}
																				src={
																					selectedOfficials?.avatar?.icon
																						? c.baseFilePath + selectedOfficials?.avatar?.icon
																						: null
																				}
																			/>
																		</WrapItem>

																		<Box mt={1}>
																			<Text textAlign="center">
																				{lang === 'kh' ? selectedOfficials?.fullNameKhmer : selectedOfficials?.fullName}
																			</Text>
																		</Box>
																	</Flex>
																)}

																<Flex>
																	<Button
																		type="button"
																		className="hidden-print"
																		onClick={() => setOpenSelectUserAllModel(true)}
																		leftIcon={
																			<FontAwesomeIcon
																				icon={selectedOfficials ? faSync : faPlusSquare}
																				style={{ fontSize: 16 }}
																			/>
																		}
																		colorScheme="blue"
																		variant="outline"
																	>
																		{selectedOfficials ? t('Change') : t('Add')}
																	</Button>
																</Flex>
															</Wrap>
														}
													/>
												</Flex>

												{openSelectUserAllModel && (
													<SelectUserAllModal
														cached={false}
														onClose={() => setOpenSelectUserAllModel(false)}
														callback={(e) => {
															setSelectedOfficials(e);
															props.setFieldValue('officials', e?.id, true);

															toast({
																title: t('You have selected a user'),
																status: 'success',
																isClosable: true,
																position: 'top-right',
															});

															setOpenSelectUserAllModel(false);
														}}
														viewType="all"
													/>
												)}
											</>
										)}

										<TextFormField {...props} label="Number" name="no" type="number" />
										<TextFormField {...props} label="Course Title" name="courseTitle" type="text" />
										<TextFormField {...props} label="Organiser" name="organiser" type="text" />
										<center>
											<DateRangeFormField {...props} label="Created Date" name="createdDate" initialDate={{ dateStart, dateEnd }} />
										</center>

										<TextFormField {...props} label="location" name="trainingLocation" type="text" />

										<SelectFormField
											{...props}
											name="department"
											label="Department"
											placeholder="Department"
											options={Array.isArray(c?.departments) ? c.departments : []}
											initialValue={department ? department : ''}
										/>

										{props?.values?.department && (
											<SelectFormField
												{...props}
												key={props?.values?.department}
												label="Office"
												name="office"
												placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
												options={OFFICE_OPTIONS_BY_DEPARTMENT[props?.values?.department] || []}
												initialValue={office ? office : ''}
											/>
										)}

										<RadioFormField
											label="Means"
											name="means"
											radios={[
												{ label: 'All', value: '' },
												{ label: 'onsiteLabel', value: 'ONSITE' },
												{
													label: 'Online',
													value: 'ONLINE',
												},
											]}
											initValue={means}
										/>

										<RadioFormField
											label="Type"
											name="type"
											radios={[
												{ label: 'All', value: '' },
												{ label: 'trainingLabel', value: 'TRAINING' },
												{
													label: 'Seminar',
													value: 'SEMINAR',
												},
											]}
											initValue={type}
										/>

										<RadioFormField
											label="Location Type"
											name="locationType"
											radios={[
												{ label: 'All', value: '' },
												{ label: 'Local', value: 'LOCAL' },
												{
													label: 'Overseas',
													value: 'OVERSEAS',
												},
											]}
											initValue={locationType}
										/>

										<CheckBoxFormField {...props} label="List Course to Bio" name="bioListing" />

										<Flex mt="32px" justifyContent="flex-end">
											<Button
												id="login-btn"
												variant="solid"
												size="md"
												h="42px"
												w="128px"
												type="submit"
												borderRadius={c.borderRadius}
												color="#3498db"
											>
												{t('Apply')}
											</Button>
											<Button
												size="md"
												h="42px"
												ml={4}
												w="128px"
												onClick={() => {
													callback('?page=1');
													onClose();
												}}
												variant="ghost"
											>
												{t('Clear')}
											</Button>
										</Flex>
									</Form>
								</Box>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default TrainingCourseFilter;
